import React from "react";
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "../../campaignModalStyles";
import { i18n } from "../../../../translate/i18n";

const INTERVAL_MIN_SEC = 30;

const IntervalTabContent = ({
  values,
  setFieldValue,
  campaignEditable,
  campaign,
  selectedContactListCount,
  contactLists,
  currentCalculatedTime,
  campaignAiChoice,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const msgIntervalVal = parseInt(values.messageInterval, 10) || 0;
  const gtIntervalVal = parseInt(values.greaterInterval, 10) || 0;
  const showMsgWarning = msgIntervalVal > 0 && msgIntervalVal < INTERVAL_MIN_SEC;
  const showGtWarning = gtIntervalVal > 0 && gtIntervalVal < INTERVAL_MIN_SEC;

  return (
    <Grid xs={12} item>
      <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: 16 }}>
        <span role="img" aria-label="Configuración">⚙️</span> Configuración de Intervalos
      </Typography>
      {campaignAiChoice === "yes" ? (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ marginBottom: 14, display: "flex", alignItems: "flex-start", gap: 8 }}
        >
          <span role="img" aria-hidden>✨</span>
          <span>{i18n.t("campaigns.dialog.ai.intervalHint")}</span>
        </Typography>
      ) : null}

      {/* Banner anti-spam */}
      <Paper
        elevation={0}
        style={{
          padding: "10px 14px",
          marginBottom: 16,
          backgroundColor: theme.palette.type === "dark" ? "#1a2a1a" : "#f0fdf4",
          border: "1px solid #22c55e",
          borderRadius: 8,
        }}
      >
        <Typography variant="caption" style={{ color: "#15803d", lineHeight: 1.6 }}>
          <strong>🛡️ Regla anti-spam WhatsApp:</strong> El tiempo mínimo entre mensajes es de{" "}
          <strong>{INTERVAL_MIN_SEC} segundos</strong>. El sistema aplica variación aleatoria (jitter)
          alrededor del valor configurado para evitar que WhatsApp detecte el envío como automático.
        </Typography>
      </Paper>

      {/* ✅ Campos en UNA SOLA FILA */}
      <Grid spacing={2} container>
        {/* Tiempo entre mensajes */}
        <Grid xs={12} sm={4} item>
          <TextField
            label="Tiempo Inicial (segundos)"
            name="messageInterval"
            type="number"
            value={values.messageInterval || 45}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 45;
              setFieldValue('messageInterval', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: INTERVAL_MIN_SEC, max: 300 }}
            error={showMsgWarning}
            helperText={showMsgWarning
              ? `⚠️ Mínimo ${INTERVAL_MIN_SEC}s — valores menores son detectados como spam por WhatsApp.`
              : `Mínimo ${INTERVAL_MIN_SEC}s recomendado anti-spam`}
            className={classes.textField}
            disabled={!campaignEditable && campaign.status !== "CANCELADA"}
          />
        </Grid>

        {/* Aplicar intervalo mayor después de X contactos */}
        <Grid xs={12} sm={4} item>
          <TextField
            label="Luego de X contactos"
            name="longerIntervalAfter"
            type="number"
            value={values.longerIntervalAfter || 20}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 20;
              setFieldValue('longerIntervalAfter', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            className={classes.textField}
            disabled={!campaignEditable && campaign.status !== "CANCELADA"}
          />
        </Grid>

        {/* Intervalo mayor entre mensajes */}
        <Grid xs={12} sm={4} item>
          <TextField
            label="Nuevo tiempo de envío"
            name="greaterInterval"
            type="number"
            value={values.greaterInterval || 60}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 60;
              setFieldValue('greaterInterval', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: INTERVAL_MIN_SEC, max: 600 }}
            error={showGtWarning}
            helperText={showGtWarning
              ? `⚠️ Mínimo ${INTERVAL_MIN_SEC}s — valores menores son detectados como spam por WhatsApp.`
              : `Mínimo ${INTERVAL_MIN_SEC}s recomendado anti-spam`}
            className={classes.textField}
            disabled={!campaignEditable && campaign.status !== "CANCELADA"}
          />
        </Grid>
      </Grid>

      {/* ✅ FASE 4: Panel de Cálculo Dinámico */}
      {/* ✅ CORRECCIÓN: Usar currentCalculatedTime (calculado en render) en lugar de calculatedTime (estado) */}
      {selectedContactListCount && selectedContactListCount > 0 && currentCalculatedTime && (
        <Grid xs={12} item style={{ marginTop: 24 }}>
          <Paper style={{ padding: 20, backgroundColor: theme.palette.background.paper }}>
            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: 16 }}>
              <span role="img" aria-label="Estadísticas">📊</span> TIEMPO DE ENVÍO ESTIMADO
            </Typography>

            <Typography variant="body2" style={{ marginBottom: 8 }}>
              <strong>Lista de contacto:</strong> {contactLists.find(list => list.id === parseInt(values.contactListId))?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 16, color: '#d32f2f', fontWeight: 'bold' }}>
              <span role="img" aria-label="Importante">🔴</span> <strong>Contiene:</strong> {selectedContactListCount} contactos
            </Typography>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
              Desglose Paso a Paso:
            </Typography>

            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <strong>Paso 1:</strong> Primeros {values.longerIntervalAfter || 20} contactos × {values.messageInterval || 45} segundos = {Math.min(values.longerIntervalAfter || 20, selectedContactListCount) * (values.messageInterval || 45)} segundos
            </Typography>

            {selectedContactListCount > (values.longerIntervalAfter || 20) && (
              <>
                <Typography variant="body2" style={{ marginBottom: 4 }}>
                  <strong>Paso 2:</strong> Contactos restantes = {selectedContactListCount} - {values.longerIntervalAfter || 20} = {selectedContactListCount - (values.longerIntervalAfter || 20)} contactos
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 4 }}>
                  {selectedContactListCount - (values.longerIntervalAfter || 20)} contactos × {values.greaterInterval || 60} segundos = {Math.max(0, selectedContactListCount - (values.longerIntervalAfter || 20) - 1) * (values.greaterInterval || 60)} segundos
                </Typography>
              </>
            )}

            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <strong>Paso 3:</strong> Tiempo total = {currentCalculatedTime.segundos} segundos
            </Typography>

            <Typography variant="body2" style={{ marginBottom: 16 }}>
              <strong>Paso 4:</strong> {currentCalculatedTime.segundos} segundos ÷ 60 = {currentCalculatedTime.minutos} minutos
              {currentCalculatedTime.horas > 0 && (
                <> = {currentCalculatedTime.horas} hora{currentCalculatedTime.horas > 1 ? 's' : ''} {currentCalculatedTime.minutosRestantes} minuto{currentCalculatedTime.minutosRestantes !== 1 ? 's' : ''}</>
              )}
            </Typography>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="h6" style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
              <span role="img" aria-label="Resultado">✅</span> Resultado final:
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 'bold', marginTop: 8 }}>
              Tiempo estimado: {currentCalculatedTime.segundos} segundos = {currentCalculatedTime.texto}
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default IntervalTabContent;
